/**
 * ScStateBadge — shared draft/confirmed indicator for scoping widgets.
 *
 * Three-state, driven by the widget's optional `draft` flag:
 *   draft === true   → purple "DRAFT" pill  (being built; usually paired with actions)
 *   draft === false  → a green tick          (locked in — a tag reads as noise once
 *                                             something is actually confirmed)
 *   draft undefined  → nothing               (opt-in; existing renders are unaffected
 *                                             until producers start emitting `draft`)
 *
 * The draft pill's geometry matches the original ScArtifactCard draft pill so it
 * reads consistently across every scoping card's header slot; the confirmed tick
 * is the same mark used for a per-bet confirm on the bets_with_assumptions card,
 * so "confirmed" looks identical everywhere it appears.
 */

import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const BASE = 'shrink-0 rounded-full border px-2 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-wide';

export function ScStateBadge({ draft, className }: { draft?: boolean; className?: string }) {
  if (draft === true) {
    return <span className={cn(BASE, 'border-[#7C3AED]/30 text-[#C084FC]', className)}>draft</span>;
  }
  if (draft === false) {
    return (
      <span
        className={cn(
          'inline-flex size-[18px] shrink-0 items-center justify-center rounded-full bg-green-500/15 text-green-500',
          className,
        )}
        title="Confirmed"
        aria-label="Confirmed"
      >
        <Check className="size-3" strokeWidth={3} />
      </span>
    );
  }
  return null;
}
